import { type FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { JSONContent } from '@tiptap/react';
import cn from 'classnames';

import { ComposerTrigger } from '~/routes/community/_components/Composer/ComposerTrigger';
import { Avatar } from '~/routes/community/_components/ui/Avatar';
import { PillButton } from '~/routes/community/_components/ui/PillButton';
import { useCommunityMembership } from '~/routes/community/_hooks/useCommunityMembership';

import { CommentEditor, type CommentEditorHandle } from './CommentEditor';

interface CommentComposerProps {
  placeholder?: string;
  initialContent?: JSONContent;
  onSubmit: (json: JSONContent) => void;
  onCancel?: () => void;
  isSubmitting: boolean;
  variant?: 'pill' | 'inline';
  spaceSlug?: string;
}

export const CommentComposer: FC<CommentComposerProps> = ({
  placeholder,
  initialContent,
  onSubmit,
  onCancel,
  isSubmitting,
  variant = 'pill',
  spaceSlug,
}) => {
  const { t } = useTranslation();
  const { currentMember } = useCommunityMembership();
  const [isExpanded, setIsExpanded] = useState(variant === 'inline');
  const [isEmpty, setIsEmpty] = useState(true);
  const editorRef = useRef<CommentEditorHandle>(null);
  const defaultPlaceholder = placeholder ?? t('community.comments.write_comment');

  const avatarUrl = currentMember?.subscription?.avatar_url ?? undefined;
  const avatarName = currentMember?.subscription
    ? `${currentMember.subscription.first_name} ${currentMember.subscription.last_name}`.trim()
    : '';

  const handleSubmit = useCallback(() => {
    if (!editorRef.current || editorRef.current.isEmpty() || isSubmitting) return;
    const json = editorRef.current.getJSON();
    onSubmit(json);
    editorRef.current.clear();
    if (variant === 'pill') setIsExpanded(false);
  }, [onSubmit, isSubmitting, variant]);

  const handleCancel = useCallback(() => {
    if (variant === 'pill') setIsExpanded(false);
    onCancel?.();
  }, [variant, onCancel]);

  if (!isExpanded) {
    return (
      <div className="flex items-center py-4">
        <ComposerTrigger
          avatarUrl={avatarUrl}
          avatarAlt={avatarName}
          placeholder={defaultPlaceholder}
          buttonLabel={t('community.comments.submit')}
          onClick={() => setIsExpanded(true)}
          ariaLabel={t('community.comments.write_comment')}
          appearance="comment"
          className="flex-1 cursor-text"
        />
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="rounded-2xl border border-black/10 bg-white shadow-[0px_0px_0px_0px_rgba(0,0,0,0.02),0px_8px_24px_0px_rgba(0,0,0,0.1)]">
        <div className="flex items-start gap-3 p-4 pb-0">
          <Avatar src={avatarUrl} alt={avatarName} className="mt-0.5 size-10" />
          <div className="min-w-0 flex-1">
            <CommentEditor
              ref={editorRef}
              placeholder={defaultPlaceholder}
              initialContent={initialContent}
              autofocus
              className="min-h-[80px]"
              onEmptyChange={setIsEmpty}
              onKeyboardSubmit={handleSubmit}
              spaceSlug={spaceSlug}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 pb-4 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            className="h-8 px-3 text-[13px] font-medium tracking-[-0.13px] text-black/50 transition-colors hover:text-black/80"
          >
            {t('community.comments.cancel')}
          </button>
          <PillButton
            variant="primary"
            size="sm"
            disabled={isSubmitting || isEmpty}
            onClick={handleSubmit}
            className={cn('transition-opacity', {
              'cursor-not-allowed opacity-50': isSubmitting || isEmpty,
            })}
          >
            {isSubmitting ? t('community.comments.submitting') : t('community.comments.submit')}
          </PillButton>
        </div>
      </div>
    </div>
  );
};
